import type { MtoRun } from '@entities/mto';
import { decisionLabel, decisionTone } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  ActionGroup,
  ActionHint,
  HeadActions,
  ReviewBar,
  ReviewText,
} from '../styles';

interface RunReviewBarProps {
  run: MtoRun;
  isLongLead: boolean;
  isSuppliers: boolean;
  isStock: boolean;
  isSupply: boolean;
  isVedomost: boolean;
  isReconcile: boolean;
  canWrite: boolean;
  deciding: boolean;
  blockingCritical: number;
  onDecision: (decision: 'accepted' | 'rejected') => void;
}

export function RunReviewBar({
  run,
  isLongLead,
  isSuppliers,
  isStock,
  isSupply,
  isVedomost,
  isReconcile,
  canWrite,
  deciding,
  blockingCritical,
  onDecision,
}: RunReviewBarProps) {
  if (run.status !== 'completed') return null;

  const decided = Boolean(run.decision);
  const approveDisabled = !canWrite || deciding || blockingCritical > 0;
  const approveHint = !canWrite
    ? 'Только просмотр'
    : blockingCritical > 0
      ? `Разберите критические расхождения (${blockingCritical})`
      : undefined;

  return (
    <ReviewBar>
      {decided ? (
        <ReviewText>
          Решение по обработке:{' '}
          <Badge $tone={decisionTone(run.decision ?? '')}>
            {decisionLabel(run.decision ?? '')}
          </Badge>
        </ReviewText>
      ) : (
        <ReviewText>
          {isLongLead
            ? 'Проверьте длинноцикловые позиции и расхождения, затем утвердите обработку.'
            : isSuppliers
              ? 'Проверьте предложения поставщиков и расхождения, затем утвердите обработку.'
              : isStock
                ? 'Проверьте остатки и расхождения, затем утвердите обработку.'
                : isSupply
                  ? 'Проверьте поставки и расхождения, затем утвердите обработку.'
                  : isVedomost
                    ? 'Проверьте строки «проверить», затем отметьте ведомость как проверенную.'
                    : isReconcile
                      ? 'Разберите расхождения с заявками, затем отметьте сверку как проверенную.'
                      : 'Проверьте паспорта и замечания, затем утвердите обработку.'}
        </ReviewText>
      )}
      {!decided && (
        <HeadActions>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => onDecision('rejected')}
            disabled={!canWrite || deciding}
            title={!canWrite ? 'Только просмотр' : undefined}
          >
            Отклонить
          </Button>
          <ActionGroup>
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              onClick={() => onDecision('accepted')}
              disabled={approveDisabled}
              title={approveHint}
              aria-describedby={approveHint ? 'mto-approve-hint' : undefined}
            >
              {deciding ? 'Сохраняю…' : 'Утвердить'}
            </Button>
            {approveHint && <ActionHint id="mto-approve-hint">{approveHint}</ActionHint>}
          </ActionGroup>
        </HeadActions>
      )}
    </ReviewBar>
  );
}
